// src/pages/Reports.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    FiTag,
    FiTool,
    FiCheckSquare,
    FiFileText,
    FiAlertTriangle
} from 'react-icons/fi';
import { useApi } from '../hooks/useApi';

const isTicketOverdue = (ticket, appointments) => {
    if (!ticket || ticket.status === 'Closed') {
        return false;
    }
    const now = new Date();
    const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
    const futureAppointments = (appointments || [])
        .filter(app => {
            const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId;
            return ticketId === ticket._id && new Date(app.startTime) > now;
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const soonestFutureAppointment = futureAppointments[0];
    if (!soonestFutureAppointment) {
        const createdAt = new Date(ticket.createdAt);
        if (now.getTime() - createdAt.getTime() > twoDaysInMs) {
            return true;
        }
    }
    const allAppointmentsForTicket = (appointments || [])
        .filter(app => {
            const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId;
            return ticketId === ticket._id;
        })
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
    if (allAppointmentsForTicket.length > 0) {
        const latestAppointmentTime = new Date(allAppointmentsForTicket[0].startTime);
        const lastUpdatedTime = new Date(ticket.updatedAt || ticket.createdAt);
        if (now.getTime() - latestAppointmentTime.getTime() > twoDaysInMs && lastUpdatedTime.getTime() < latestAppointmentTime.getTime()) {
            return true;
        }
    }
    return false;
};

// --- Helper Sub-Components (Unchanged) ---

const StatCard = ({ label, value, icon, colorClass }) => (
    <div className="stat-card">
        <div className="stat-card-header">
            <div className={`stat-card-icon ${colorClass}`}>{icon}</div>
            <span className="stat-card-label">{label}</span>
        </div>
        <p className="stat-card-value">{value}</p>
    </div>
);

const ChartCard = ({ title }) => (
    <div className="card">
        <div className="card-header">
            <h3 className="card-title">{title}</h3>
        </div>
        <div className="card-body chart-placeholder">
            <span>Chart Placeholder</span>
        </div>
    </div>
);

// --- Main Reports Component (UPDATED) ---

const Reports = () => {
    const callApi = useApi();
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // ✅ UPDATED: Added 'sites' parameter to function signature
    const generateReportStats = useCallback((tickets, technicians, inventory, appointments, sites) => {
        const openTicketsCount = tickets.filter(t => t.status === 'Open').length;
        const overdueTicketsCount = tickets.filter(t => isTicketOverdue(t, appointments)).length;
        const activeTechniciansCount = technicians.filter(t => t.status === 'Active').length;
        const completedJobsCount = tickets.filter(t => t.status === 'Closed').length;
        
        // ✅ NEW: Filter Low Stock Items (Office Only)
        const lowStockItemsCount = inventory.filter(i => {
            // Must have a reorder level defined
            if (i.reorderLevel === null || i.reorderLevel === undefined) return false;
            // Must be below threshold
            if (i.quantity > i.reorderLevel) return false;
            
            // Must be an Office Site
            if (i.ownerType === 'Site') {
                const site = sites.find(s => s._id === i.ownerId);
                return site && site.type === 'Office';
            }
            return false;
        }).length;

        setStats([
            { label: "Open Tickets", value: openTicketsCount.toLocaleString(), icon: <FiTag />, colorClass: "icon-blue" },
            { label: "Overdue Tickets", value: overdueTicketsCount.toLocaleString(), icon: <FiTag />, colorClass: "icon-red" },
            { label: "Active Technicians", value: activeTechniciansCount.toLocaleString(), icon: <FiTool />, colorClass: "icon-gray" },
            { label: "Total Jobs Completed", value: completedJobsCount.toLocaleString(), icon: <FiCheckSquare />, colorClass: "icon-green" },
            { label: "Low Stock Items", value: lowStockItemsCount.toLocaleString(), icon: <FiAlertTriangle />, colorClass: "icon-orange" },
            { label: "Revenue (This Month)", value: "$15,430.50", icon: <FiFileText />, colorClass: "icon-green" },
            { label: "Outstanding Amount", value: "$4,820.00", icon: <FiFileText />, colorClass: "icon-orange" },
        ]);
    }, []);

    useEffect(() => {
        const fetchReportData = async () => {
            setIsLoading(true);
            try {
                // ✅ UPDATED: Fetch sites as well
                const [ticketsResult, techniciansResult, inventoryResult, appointmentsResult, sitesResult] = await Promise.all([
                    callApi('tickets?limit=1000'), // Fetch all tickets for stats
                    callApi('technicians'),
                    callApi('inventory'),
                    callApi('appointments'),
                    callApi('sites') // <-- Fetch Sites
                ]);

                if (ticketsResult.success && techniciansResult.success && inventoryResult.success && appointmentsResult.success && sitesResult.success) {
                    // ✅ Pass sites.data to generator
                    generateReportStats(
                        ticketsResult.data.data, 
                        techniciansResult.data, 
                        inventoryResult.data, 
                        appointmentsResult.data, 
                        sitesResult.data
                    );
                } else {
                    console.error("Failed to load one or more data sources for reports.");
                }
            } catch (error) {
                console.error("Error fetching report data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReportData();
    }, [callApi, generateReportStats]);
    
    const chartCardsData = [
        { title: "Ticket Volume Over Time" },
        { title: "Technician Performance" },
    ];

    if (isLoading || !stats) {
        return <div className="loading-indicator">Generating reports...</div>;
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <h2 className="page-title">Reports Dashboard</h2>
            </div>
            <div className="reports-grid">
                <div className="stats-grid">
                    {stats.map((card, index) => (
                        <StatCard key={index} {...card} />
                    ))}
                </div>
                <div className="charts-grid">
                    {chartCardsData.map((card, index) => (
                        <ChartCard key={index} title={card.title} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Reports;